// src/utils/extractors.js

// ------------------------
// HTML helpers
// ------------------------
export function parseHtmlString(html) {
  const parser = new DOMParser();
  // Wrap in a container so partial HTML (body-only) still parses safely
  const doc = parser.parseFromString(`<div id="__wrap__">${html || ""}</div>`, "text/html");
  const wrap = doc.getElementById("__wrap__");
  return { doc, wrap };
}

// Extract all unique class tokens from HTML
export function extractAllClassNames(html) {
  const { wrap } = parseHtmlString(html);
  const set = new Set();

  if (!wrap) return [];

  wrap.querySelectorAll("*").forEach((el) => {
    const cls = (el.getAttribute("class") || "").trim();
    if (!cls) return;
    cls.split(/\s+/).forEach((c) => c && set.add(c));
  });

  return Array.from(set);
}

// Return the FIRST matching element outerHTML for a selector
export function extractHtmlBySelector(html, selector) {
  if (!selector || typeof selector !== "string") return "";

  const { wrap } = parseHtmlString(html);
  if (!wrap) return "";

  // If user passes "hero-1" (class name), convert to ".hero-1"
  const normalized = selector.startsWith(".") || selector.includes(" ") || selector.includes("#")
    ? selector
    : `.${selector}`;

  const el = wrap.querySelector(normalized);
  return el ? el.outerHTML : "";
}

// Return ALL matching elements outerHTML for a selector
export function extractAllHtmlBySelector(html, selector) {
  if (!selector || typeof selector !== "string") return [];

  const { wrap } = parseHtmlString(html);
  if (!wrap) return [];

  const normalized = selector.startsWith(".") || selector.includes(" ") || selector.includes("#")
    ? selector
    : `.${selector}`;

  return Array.from(wrap.querySelectorAll(normalized)).map((el) => el.outerHTML);
}

// ------------------------
// NEW HTML context helpers
// ------------------------

function normalizeHtmlSelector(selector) {
  if (!selector || typeof selector !== "string") return "";
  return selector.startsWith(".") || selector.includes(" ") || selector.includes("#")
    ? selector
    : `.${selector}`;
}

function getElementBySelector(html, selector) {
  const { wrap } = parseHtmlString(html);
  if (!wrap) return null;

  const normalized = normalizeHtmlSelector(selector);
  if (!normalized) return null;

  return wrap.querySelector(normalized);
}

// Return the matched element's parent outerHTML
export function extractParentHtmlBySelector(html, selector) {
  const el = getElementBySelector(html, selector);
  if (!el) return "";

  const parent = el.parentElement;
  if (!parent) return "";

  // avoid returning the artificial wrapper
  if (parent.id === "__wrap__") return "";

  return parent.outerHTML;
}

// Return all direct children outerHTML of the matched element
export function extractChildrenHtmlBySelector(html, selector) {
  const el = getElementBySelector(html, selector);
  if (!el) return [];

  return Array.from(el.children).map((child) => child.outerHTML);
}

// Return all direct siblings outerHTML of the matched element (excluding itself)
export function extractSiblingsHtmlBySelector(html, selector) {
  const el = getElementBySelector(html, selector);
  if (!el || !el.parentElement) return [];

  return Array.from(el.parentElement.children)
    .filter((sibling) => sibling !== el)
    .map((sibling) => sibling.outerHTML);
}

// Return a compact context object for the matched selector
export function extractHtmlContextBySelector(html, selector) {
  const el = getElementBySelector(html, selector);
  if (!el) {
    return {
      selector,
      html: "",
      parentHtml: "",
      childrenHtml: [],
      siblingsHtml: [],
    };
  }

  const parent = el.parentElement && el.parentElement.id !== "__wrap__"
    ? el.parentElement.outerHTML
    : "";

  const children = Array.from(el.children).map((child) => child.outerHTML);

  const siblings = el.parentElement
    ? Array.from(el.parentElement.children)
        .filter((sibling) => sibling !== el)
        .map((sibling) => sibling.outerHTML)
    : [];

  return {
    componentHtml: el.outerHTML,
    parentHtml: parent,
    childrenHtml: children,
    siblingsHtml: siblings,
  };
}

// ------------------------
// CSS helpers
// ------------------------

// Finds the matching closing brace index for a block starting at openBraceIndex ("{")
function findMatchingBrace(css, openBraceIndex) {
  let depth = 0;
  for (let i = openBraceIndex; i < css.length; i++) {
    const ch = css[i];
    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

// Extract normal rules "SELECTOR { ... }" from a CSS string
// - selectorQuery can be "k-hero1", ".k-hero1", ".k-hero1-hd1", ".k-hero1 .cta", ":root", "body", ".page"
export function extractCssBlocksForSelector(css, selectorQuery) {
  if (!css || !selectorQuery) return "";

  let selector = selectorQuery.trim();
  if (!selector) return "";

  // If user passes "k-hero1" (class name), convert to ".k-hero1"
  if (
    !selector.startsWith(".") &&
    !selector.includes(" ") &&
    !selector.includes("#") &&
    !selector.startsWith(":")
  ) {
    selector = `.${selector}`;
  }

  const out = [];

  // 1) Extract top-level rules
  out.push(...extractCssBlocksInChunk(css, selector));

  // 2) Extract rules inside @media blocks as well
  const mediaRegex = /@media[^{]+\{/g;
  let m;
  while ((m = mediaRegex.exec(css)) !== null) {
    const mediaStart = m.index;
    const mediaOpenBrace = css.indexOf("{", mediaStart);
    if (mediaOpenBrace < 0) continue;

    const mediaEnd = findMatchingBrace(css, mediaOpenBrace);
    if (mediaEnd < 0) continue;

    const mediaHeader = css.slice(mediaStart, mediaOpenBrace).trim();
    const mediaInner = css.slice(mediaOpenBrace + 1, mediaEnd);

    const innerMatches = extractCssBlocksInChunk(mediaInner, selector);
    if (innerMatches.length) {
      out.push(`${mediaHeader} {\n${innerMatches.join("\n\n")}\n}`);
    }
  }

  // De-dupe identical blocks
  return Array.from(new Set(out)).join("\n\n");
}

// Extract selector blocks inside a chunk of css (no @media wrapping)
function extractCssBlocksInChunk(cssChunk, selector) {
  const out = [];
  const css = cssChunk || "";

  const normalizedTarget = selector.replace(/\s+/g, " ").trim();

  for (let i = 0; i < css.length; i++) {
    if (css[i] !== "{") continue;

    // Find selector start by walking backwards to previous "}"
    const prevClose = css.lastIndexOf("}", i);
    const selectorText = css.slice(prevClose + 1, i).trim();

    if (!selectorText) continue;

    const selectorList = selectorText
      .split(",")
      .map((s) => s.replace(/\s+/g, " ").trim())
      .filter(Boolean);

    const matches = selectorList.some((s) => {
      // direct match
      if (s === normalizedTarget) return true;

      // descendant / contextual selector
      if (s.endsWith(` ${normalizedTarget}`)) return true;

      // pseudo-classes directly on target
      if (s.startsWith(`${normalizedTarget}:`)) return true;

      // pseudo-classes when target appears later in chain
      if (s.endsWith(` ${normalizedTarget}:hover`)) return true;
      if (s.endsWith(` ${normalizedTarget}:focus`)) return true;
      if (s.endsWith(` ${normalizedTarget}:active`)) return true;
      if (s.endsWith(` ${normalizedTarget}:visited`)) return true;
      if (s.endsWith(` ${normalizedTarget}:disabled`)) return true;
      if (s.endsWith(` ${normalizedTarget}:checked`)) return true;
      if (s.endsWith(` ${normalizedTarget}:selected`)) return true;
      if (s.endsWith(` ${normalizedTarget}:focus-visible`)) return true;
      if (s.endsWith(` ${normalizedTarget}:focus-within`)) return true;
      if (s.endsWith(` ${normalizedTarget}:first-child`)) return true;
      if (s.endsWith(` ${normalizedTarget}:last-child`)) return true;

      // handles nth-child(...) and similar
      if (s.includes(`${normalizedTarget}:nth-child(`)) return true;
      if (s.includes(`${normalizedTarget}:nth-of-type(`)) return true;

      // pseudo-elements directly on target
      if (s.startsWith(`${normalizedTarget}::`)) return true;

      // pseudo-elements when target appears later in chain
      if (s.endsWith(` ${normalizedTarget}::before`)) return true;
      if (s.endsWith(` ${normalizedTarget}::after`)) return true;
      if (s.endsWith(` ${normalizedTarget}::placeholder`)) return true;
      if (s.endsWith(` ${normalizedTarget}::marker`)) return true;
      if (s.endsWith(` ${normalizedTarget}::selection`)) return true;
      return false;
    });

    if (!matches) continue;

    const close = findMatchingBrace(css, i);
    if (close < 0) continue;

    const block = `${selectorText} ${css.slice(i, close + 1)}`.trim();
    out.push(block);

    i = close;
  }

  return out;
}

// ------------------------
// NEW CSS context helpers
// ------------------------

function getElementCssSelectors(el) {
  if (!el) return [];

  const selectors = [];

  // Add all classes as selectors
  if (el.classList && el.classList.length > 0) {
    selectors.push(...Array.from(el.classList).map((c) => `.${c}`));
  }

  // Add id if present
  const id = (el.getAttribute("id") || "").trim();
  if (id) selectors.push(`#${id}`);

  return Array.from(new Set(selectors));
}

function contextFindMatchingBrace(str, openIndex) {
  if (!str || str[openIndex] !== "{") return -1;

  let depth = 0;
  for (let i = openIndex; i < str.length; i++) {
    if (str[i] === "{") depth++;
    else if (str[i] === "}") depth--;

    if (depth === 0) return i;
  }

  return -1;
}

// Recursively extract matching CSS blocks, including inside @media, @supports, @layer, etc.
function extractCssBlocksRecursive(cssChunk, selector) {
  const out = [];
  const css = cssChunk || "";
  const normalizedTarget = (selector || "").replace(/\s+/g, " ").trim();

  if (!normalizedTarget) return out;

  for (let i = 0; i < css.length; i++) {
    if (css[i] !== "{") continue;

    const prevClose = css.lastIndexOf("}", i);
    const selectorText = css.slice(prevClose + 1, i).trim();
    if (!selectorText) continue;

    const close = contextFindMatchingBrace(css, i);
    if (close < 0) continue;

    const body = css.slice(i + 1, close);
    const trimmedSelectorText = selectorText.replace(/\s+/g, " ").trim();

    // Handle @media, @supports, @layer, @container, etc. recursively
    if (trimmedSelectorText.startsWith("@")) {
      const innerMatches = extractCssBlocksRecursive(body, normalizedTarget);
      if (innerMatches.length) {
        out.push(`${selectorText} {\n${innerMatches.join("\n\n")}\n}`);
      }
      i = close;
      continue;
    }

    const selectorList = trimmedSelectorText
      .split(",")
      .map((s) => s.replace(/\s+/g, " ").trim())
      .filter(Boolean);

    const matches = selectorList.some((s) => {
      // direct match
      if (s === normalizedTarget) return true;

      // descendant / contextual selector
      if (s.endsWith(` ${normalizedTarget}`)) return true;

      // pseudo-classes directly on target
      if (s.startsWith(`${normalizedTarget}:`)) return true;

      // pseudo-classes when target appears later in chain
      if (s.endsWith(` ${normalizedTarget}:hover`)) return true;
      if (s.endsWith(` ${normalizedTarget}:focus`)) return true;
      if (s.endsWith(` ${normalizedTarget}:active`)) return true;
      if (s.endsWith(` ${normalizedTarget}:visited`)) return true;
      if (s.endsWith(` ${normalizedTarget}:disabled`)) return true;
      if (s.endsWith(` ${normalizedTarget}:checked`)) return true;
      if (s.endsWith(` ${normalizedTarget}:selected`)) return true;
      if (s.endsWith(` ${normalizedTarget}:focus-visible`)) return true;
      if (s.endsWith(` ${normalizedTarget}:focus-within`)) return true;
      if (s.endsWith(` ${normalizedTarget}:first-child`)) return true;
      if (s.endsWith(` ${normalizedTarget}:last-child`)) return true;

      // handles nth-child(...) and similar
      if (s.includes(`${normalizedTarget}:nth-child(`)) return true;
      if (s.includes(`${normalizedTarget}:nth-of-type(`)) return true;

      // pseudo-elements directly on target
      if (s.startsWith(`${normalizedTarget}::`)) return true;

      // pseudo-elements when target appears later in chain
      if (s.endsWith(` ${normalizedTarget}::before`)) return true;
      if (s.endsWith(` ${normalizedTarget}::after`)) return true;
      if (s.endsWith(` ${normalizedTarget}::placeholder`)) return true;
      if (s.endsWith(` ${normalizedTarget}::marker`)) return true;
      if (s.endsWith(` ${normalizedTarget}::selection`)) return true;

      return false;
    });

    if (matches) {
      out.push(`${selectorText} {\n${body.trim()}\n}`);
    }

    i = close;
  }

  return out;
}

function dedupeCssBlocks(blocks) {
  return Array.from(
    new Set(
      (blocks || [])
        .flat()
        .map((b) => (b || "").trim())
        .filter(Boolean)
    )
  );
}

function extractCssForElement(css, el) {
  if (!el) return "";

  const selectors = getElementCssSelectors(el);

  const blocks = selectors.flatMap((selector) => {
    const recursiveBlocks = extractCssBlocksRecursive(css, selector);

    // keep your old helper if it exists somewhere else in your codebase
    const directBlocks =
      typeof extractCssBlocksForSelector === "function"
        ? [extractCssBlocksForSelector(css, selector)].filter(Boolean)
        : [];

    return [...directBlocks, ...recursiveBlocks];
  });

  return dedupeCssBlocks(blocks).join("\n\n");
}

function getElementBySelectorForCss(html, selector) {
  if (!selector || typeof selector !== "string") return null;

  const { wrap } = parseHtmlString(html);
  if (!wrap) return null;

  const normalized =
    selector.startsWith(".") || selector.includes(" ") || selector.includes("#")
      ? selector
      : `.${selector}`;

  return wrap.querySelector(normalized);
}

// Return CSS for the matched element itself
export function extractComponentCssBySelector(html, css, selector) {
  const el = getElementBySelectorForCss(html, selector);
  return extractCssForElement(css, el);
}

// Return CSS for the matched element's parent
export function extractParentCssBySelector(html, css, selector) {
  const el = getElementBySelectorForCss(html, selector);
  if (!el || !el.parentElement || el.parentElement.id === "__wrap__") return "";

  return extractCssForElement(css, el.parentElement);
}

// Return CSS for all direct children of the matched element
export function extractChildrenCssBySelector(html, css, selector) {
  const el = getElementBySelectorForCss(html, selector);
  if (!el) return [];

  return Array.from(el.children).map((child) => ({
    selectors: getElementCssSelectors(child),
    css: extractCssForElement(css, child),
  }));
}

// Return CSS for all direct siblings of the matched element
export function extractSiblingsCssBySelector(html, css, selector) {
  const el = getElementBySelectorForCss(html, selector);
  if (!el || !el.parentElement) return [];

  return Array.from(el.parentElement.children)
    .filter((sibling) => sibling !== el)
    .map((sibling) => ({
      selectors: getElementCssSelectors(sibling),
      css: extractCssForElement(css, sibling),
    }));
}

// Return grouped CSS context for the matched element, parent, children, and siblings
export function extractCssContextBySelector(html, css, selector) {
  return {
    componentCss: extractComponentCssBySelector(html, css, selector),
    parentCss: extractParentCssBySelector(html, css, selector),
    childrenCss: extractChildrenCssBySelector(html, css, selector),
    siblingsCss: extractSiblingsCssBySelector(html, css, selector),
  };
}

// ------------------------
// NEW: Global CSS extractor
// ------------------------
export function extractCssGlobalBlocks(css) {
  if (!css || typeof css !== "string") return "";

  const globalSelectors = [":root", "html", "body", ".page"];
  const out = [];

  for (const selector of globalSelectors) {
    const blocks = extractCssBlocksForSelector(css, selector);
    if (blocks) out.push(blocks);
  }

  // De-dupe identical blocks
  return Array.from(new Set(out)).join("\n\n");
}




export function buildEditContextFromPlan({ page, plan }) {
  const html = page?.html || "";
  const css = page?.css || "";
  const pageSummary = page?.pageSummary || "";

  const globalCss = extractCssGlobalBlocks(css);

  const htmlContext = (plan?.targetSelectors || [])
  .map((selector) => ({
    selector,
    html: extractHtmlContextBySelector(html, selector),
  })).filter((block) => block.html);

  const cssContext = (plan?.targetSelectors || [])
    .map((selector) => ({
      selector,
      css: extractCssContextBySelector(html, css, selector),
    })).filter((block) => block.css);

  return {
    editType: plan?.editType || "Unknown",
    pageSummary,
    globalCss,
    htmlContext,
    cssContext,
    position: plan?.position || "",
  };
}